<template>
  <div class="compatibility-form-content-wrapper">
    <span
      v-if="caseNotFound"
      id="details-plate"
      class="details-plate-disabled"
    >
      {{ plateValue }}
    </span>
    <v-text-field
      v-else
      id="details-plate"
      v-model="plateValue"
      :label="$t('checker.legends.plate')"
      single-line
      outlined
      dark
      dense
      hide-details
      :color="colors.secondary"
      class="details-plate"
    />
    <v-select
      id="details-brand"
      v-model="selectedBrand"
      :items="brands"
      :label="$t('checker.legends.brand')"
      solo
      dense
      size="small"
      :menu-props="{ top: true, offsetY: true }"
      dark
      :outlined="true"
      class="details-brand"
      @change="onBrandChange"
    />
    <v-select
      id="details-model"
      v-model="selectedModel"
      :items="models"
      :label="$t('checker.legends.model')"
      solo
      dense
      size="small"
      :menu-props="{ top: true, offsetY: true }"
      dark
      :outlined="true"
      class="details-model"
      :disabled="!selectedBrand"
    />
    <DatePicker
      id="details-registration"
      v-model="registrationDate"
      class="details-picker"
      mode="month"
      format="MM/YYYY"
      :placeholder="$t('checker.legends.reg')"
      :showToday="false"
      :open="registrationPickerOpen"
      @openChange="registrationPickerOpen = $event"
      @panelChange="handleRegistrationChange"
    />
    <DatePicker
      id="details-calibration"
      v-model="calibrationDate"
      class="details-picker"
      :placeholder="$t('checker.legends.calibration')"
      mode="month"
      format="MM/YYYY"
      :open="calibrationPickerOpen"
      @openChange="calibrationPickerOpen = $event"
      @panelChange="handleCalibrationChange"
    />
    <div class="checkbox-container">
      <v-checkbox
        id="details-tachograph"
        v-model="hasTachograph"
        dark
        :ripple="false"
        on-icon="mdi-checkbox-intermediate"
        :color="colors.secondary"
        hide-details="true"
      />
      <span>{{ $t('checker.vdo_tachograph') }}</span>
    </div>
    <div class="checkbox-container">
      <v-checkbox
        id="details-transport"
        v-model="hasTransport"
        dark
        :ripple="false"
        on-icon="mdi-checkbox-intermediate"
        :color="colors.secondary"
        hide-details="true"
      />
      <span>{{ $t('checker.legends.transport') }}</span>
    </div>
    <v-btn
      id="details-check-compat"
      :title="$t('checker.check')"
      class="check-compat ctp-btn"
      dark
      rounded
      :disabled="!isFormValid"
      @click="searchByDetails"
    >
      <div class="apply_btn_content">
        <span>{{ $t('checker.check') }}</span>
      </div>
    </v-btn>
  </div>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue';
import { DatePicker } from 'ant-design-vue';
import { colors } from '@/constants/theme/colors';
import { brandModels, brands } from '../constants/brandModels';
import './styles/compatibility-form.scss';

const props = defineProps<{
  plate?: string;
  caseNotFound?: boolean;
}>();

const plateValue = ref(props.plate || '');

const selectedBrand = ref<string | null>(null);
const selectedModel = ref<string | null>(null);

const models = computed(() => {
  if (!selectedBrand.value) return [];
  return brandModels[selectedBrand.value] || [];
});

const onBrandChange = () => {
  selectedModel.value = null;
};

const calibrationDate = ref<moment.Moment | null>(null);
const registrationDate = ref<moment.Moment | null>(null);
const calibrationPickerOpen = ref(false);
const registrationPickerOpen = ref(false);
const hasTachograph = ref(false);
const hasTransport = ref(false);

const emit = defineEmits(['searchDetails']);

const handleCalibrationChange = (value: moment.Moment | null) => {
  calibrationDate.value = value;
  calibrationPickerOpen.value = false;
};

const handleRegistrationChange = (value: moment.Moment | null) => {
  registrationDate.value = value;
  registrationPickerOpen.value = false;
};

const searchByDetails = () => {
  emit('searchDetails', {
    licencePlateNumber: plateValue.value,
    brand: selectedBrand.value,
    model: selectedModel.value,
    registrationDate: registrationDate.value ? registrationDate.value.unix() : null,
    lastCalibration: calibrationDate.value ? calibrationDate.value.unix() : null,
    vdoTachographInstalled: hasTachograph.value,
    internationalTransport: hasTransport.value
  });

  // Reinit all fields
  plateValue.value = '';
  selectedBrand.value = null;
  selectedModel.value = null;
  registrationDate.value = null;
  calibrationDate.value = null;
  hasTachograph.value = false;
  hasTransport.value = false;
  calibrationPickerOpen.value = false;
  registrationPickerOpen.value = false;
};

// Licence plate and last calibration date are optional; only brand, model
// and registration date are required to run a details-based compatibility check.
const isFormValid = computed(() => !!(
  selectedBrand.value
    && selectedModel.value
    && registrationDate.value
));
</script>
